import { useEffect, useState } from 'react'
import { useThree } from '@react-three/fiber'

import { toMB } from '@/utils'
import { arcus, KEYS } from '@/lib/arcus'
import { useCADPageStore } from '@/state'
import { GLTFObject } from '@/lib/cad/GLTFObject'
import {
  DEFAULT_LAYER,
  INTERACTION_LAYER,
  WIREFRAME_LAYER,
} from '@/pages/CADPage/constants'
import { useAssemblyTreeQuery } from '@/pages/CADPage/queries'
import { useCanvasContext } from '@/lib/cad/CanvasProvider'
// #TODO: Use rawGLTF.parser.associations for loading progress

export const useModelSetup = (
  gltf?: GLTFObject,
  options?: {
    mergeModelParts?: boolean
    computeBoundsTree?: boolean
  },
) => {
  const {
    data: { assemblyTree },
  } = useAssemblyTreeQuery()
  const camera = useThree((state) => state.camera)
  const raycaster = useThree((state) => state.raycaster)

  const [initialized, setInitialized] = useState(false)
  const { setGLTF, gltf: cachedGLTF } = useCanvasContext()

  // Reset initialization state when GLTF changes (e.g., on cadVersion change)
  useEffect(() => {
    if (gltf !== cachedGLTF) {
      setInitialized(false)
    }
  }, [gltf, cachedGLTF])

  useEffect(() => {
    if (!gltf || !assemblyTree || initialized) return

    const mergeModelParts = options?.mergeModelParts ?? true
    const computeBoundsTree = options?.computeBoundsTree ?? true

    raycaster.layers.set(INTERACTION_LAYER)
    raycaster.firstHitOnly = true

    camera.layers.disableAll()
    camera.layers.enable(DEFAULT_LAYER)
    camera.layers.enable(INTERACTION_LAYER)
    camera.layers.enable(WIREFRAME_LAYER)

    setInitialized(true)

    gltf.setup(assemblyTree, {
      mergeModelParts,
      computeBoundsTree,
    })

    // Cache the newly setup GLTF
    setGLTF(gltf)

    useCADPageStore.setState({ cadLoaded: true })
    arcus.end(KEYS.MODEL_LOADED, { totalMB: toMB(gltf.totalBytes) })

    return () => {
      if (gltf) {
        gltf.cleanupModel({ computeBoundsTree })
      }
    }
  }, [gltf, assemblyTree, options, raycaster, camera, initialized, setGLTF])
}
